import React, { Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from "react-router-dom";
import PageLoader from "../shared/pageLoader/PageLoader";
import ProtectedRoute from "./ProtectedRoute";
import FAQPage from "../pages/components/home/faqs/FAQPage";
import NotPageFound from "../pages/components/notFound/NotFoundPage";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import TermsAndConditions from "../pages/TermsAndConditions";
import CopyrightPolicy from "../pages/CopyrightPolicy";
import HyperlinkPolicy from "../pages/HyperlinkPolicy";
import CategoryPreview from "../pages/components/dashBoard/CategoryPreview";
import QuestionPreview from "../pages/components/dashBoard/QuestionPreview";
import AboutSHVR from "../pages/components/about/about";
import ContactUs from "../pages/components/contact/contact";
import SHVRProgress from "../pages/components/progress/progress";
import PagesLayout from "../layouts/pageLayout/pageLayout";
import ProgressDistrict from "../pages/components/progress/progressDistrict";
import ComingSoon from "../pages/components/notFound/ComingSoon";
import { RouterErrorFallback } from "../shared/components/ErrorBoundary";
import ProtectedAdminRoute from "./ProtectedAdminRoute.tsx";
import { ThemeProviderWrapper } from "../context/ThemeContext.tsx";
import Resources from "../pages/components/home/resources/Resources.tsx";
import ProtectedEvaluatorRoute from "./ProtectedEvaluatorRoute.tsx";
import Maintenance from "../pages/components/maintenance/Maintenance.tsx";
import CompleteSurvey from "../pages/components/CompleteSurvey/CompleteSurvey.tsx";
import DownloadCertificate from "../pages/components/CompleteSurvey/certificates/DownloadCertificate.tsx";

const RegionalUserLayout = React.lazy(() => import("../layouts/RegionalUserLayout/RegionalUserLayout"));
const SchoolList = React.lazy(() => import("../pages/components/regionalUser/SchoolList"));
const Reports = React.lazy(() => import("../pages/components/regionalUser/Reports"));
const RegionalUser = React.lazy(() => import("../pages/components/regionalUser/regionalUser"));
const SchoolSurveyDetail = React.lazy(() => import("../pages/components/regionalUser/SchoolSurveyDetail"));
const SchoolEvaluationDetail = React.lazy(() => import("../pages/components/regionalUser/SchoolEvaluationDetail"));
const TrackerTable = React.lazy(() => import("../pages/components/regionalUser/tables/TrackerTable"));
const NodalOfficerForm = React.lazy(() => import("../pages/components/regionalUser/userComponents/nodalOfficer/NodalOfficerForm"));
const Evaluators = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluators/Evaluators"));
const EvaluatorsForm = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluators/EvaluatorsForm"));
const NodalOfficers = React.lazy(() => import("../pages/components/regionalUser/userComponents/nodalOfficer/NodalOfficers"));
const DistrictNodalOfficers = React.lazy(() => import("../pages/components/regionalUser/userComponents/districtNodalOfficer/DistrictNodalOfficers"));
const NationalUser = React.lazy(() => import("../pages/components/regionalUser/nationalUser/NationalUser"));
const StateUser = React.lazy(() => import("../pages/components/regionalUser/stateUser/StateUser"));
const DistrictNodalOfficersForm = React.lazy(() => import("../pages/components/regionalUser/userComponents/districtNodalOfficer/DistrictNodalOfficersForm"));
const CommitteeMembers = React.lazy(() => import("../pages/components/regionalUser/userComponents/committeeMember/CommitteeMember"));
const CommitteeMemberForm = React.lazy(() => import("../pages/components/regionalUser/userComponents/committeeMember/CommitteeMemberForm"));
const DistrictNodalOfficersView = React.lazy(() => import("../pages/components/regionalUser/userComponents/districtNodalOfficer/DistrictNodalOfficerView"));
const StateNodalOfficers = React.lazy(() => import("../pages/components/regionalUser/userComponents/stateNodalOfficer/StateNodalOfficers"));
const StateNodalOfficersForm = React.lazy(() => import("../pages/components/regionalUser/userComponents/stateNodalOfficer/StateNodalOfficersForm"));
const StateNodalOfficersView = React.lazy(() => import("../pages/components/regionalUser/userComponents/stateNodalOfficer/StateNodalOfficersView"));
const EvaluatorsView = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluators/EvaluatorsView"));
const CommitteeMemberView = React.lazy(() => import("../pages/components/regionalUser/userComponents/committeeMember/CommitteeMemberView"));
const AssignSchool = React.lazy(() => import("../pages/components/regionalUser/userComponents/assignSchool/AssignSchool"));
const AssignSchoolView = React.lazy(() => import("../pages/components/regionalUser/userComponents/assignSchool/AssignSchoolView"));
const EligibleSchools = React.lazy(() => import("../pages/components/regionalUser/userComponents/eligibleSchools/EligibleSchools"));
const DistrictUser = React.lazy(() => import("../pages/components/regionalUser/districtUser/DistrictUser"));
const EvaluatorLayout = React.lazy(() => import("../layouts/evaluatorLayout/EvaluatorLayout"));
const Dashboard = React.lazy(() => import("../pages/components/evaluators/Dashboard"));
const NodalOfficerView = React.lazy(() => import("../pages/components/regionalUser/userComponents/nodalOfficer/NodalOfficerView"));
const ReviewSchoolSubmission = React.lazy(() => import("../pages/components/evaluators/ReviewSchoolSubmission"));
const EvaluatorTracker = React.lazy(() => import("../pages/components/regionalUser/tables/evaluatorReport/EvaluatorTracker"));
const CommitteMemberTracker = React.lazy(() => import("../pages/components/regionalUser/tables/committememberReport/CommitteMemberTracker"));
const UserManual = React.lazy(() => import("../pages/components/userManual/UserManual"));
const CommittieMemberIndividualView = React.lazy(() => import("../pages/components/regionalUser/tables/committememberReport/CommitteMemberView"));
const EvaluatorIndividualView = React.lazy(() => import("../pages/components/regionalUser/tables/evaluatorReport/EvaluatorIndividualView"));
const DistrictNodalEvaluationReport = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluationReport/DistrictNodalEvaluationReport"));
const DetailedReport = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluationReport/DetailedReport"));
const StateWiseEvaluationStatus = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluationReport/StateWiseEvaluationStatus"));
const DistrictWiseEvaluationStatus = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluationReport/DistrictWiseEvaluationStatus"));
const Evaluation = React.lazy(() => import("../pages/components/regionalUser/userComponents/evaluationReport/Evaluation"));
const RecognitionReport = React.lazy(() => import("../pages/components/regionalUser/RecognitionReport"));
const RecognitionSelectSchool = React.lazy(() => import("../pages/components/regionalUser/recognition/RecognitionSelectSchool").then((module) => ({ default: module.RecognitionSelectSchool })));
const ReportsTracker = React.lazy(() => import("../pages/components/regionalUser/reportsTracker/ReportsTracker"));
const RecognitionEligibleSchools = React.lazy(() => import("../pages/components/regionalUser/recognition/RecognitionEligibleSchools"));
const DistrictRecognitionReportStateWise = React.lazy(() => import("../pages/components/regionalUser/recognition/DistrictRecognitionReportStateWise"));
const DistrictRecognitionReportDistrictWise = React.lazy(() => import("../pages/components/regionalUser/recognition/DistrictRecognitionReportDistrictWise"));
const StateRecognitionReportStateWise = React.lazy(() => import("../pages/components/regionalUser/recognition/StateRecognitionReportStateWise"));
const ViewSchools = React.lazy(() => import("../pages/components/regionalUser/userComponents/viewSchools/ViewSchools"));
const ReportCatd = React.lazy(() => import("../pages/components/regionalUser/reportCard/ReportCatd"));
const ArchivedPortal = React.lazy(() => import("../pages/components/regionalUser/archivedPortal/ArchivedPortal"));
const ArchivedDashboard = React.lazy(() => import("../pages/components/regionalUser/archivedPortal/pages/ArchivedDashboard"));
const ArchivedReports = React.lazy(() => import("../pages/components/regionalUser/archivedPortal/pages/ArchivedReports"));
const ArchivedRecognition = React.lazy(() => import("../pages/components/regionalUser/archivedPortal/pages/ArchivedRecognition"));
const ArchivedReportCard = React.lazy(() => import("../pages/components/regionalUser/archivedPortal/pages/ArchivedReportCard"));
// import RecognizedSchoolsNational from "../pages/RecognizedSchoolsNational.tsx";

const ScrollToTopWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <ScrollRestoration />
      {children}
    </>
  );
};

const AppRoutes = () => {
  const Home = React.lazy(() => import("../pages/components/home/Home"));
  const WebLayout = React.lazy(() => import("../layouts/webLayout/WebLayout"));
  const AuthLayout = React.lazy(
    () => import("../layouts/authLayout/AuthLayout")
  );
  const SignIn = React.lazy(() => import("../pages/components/auth/SignUp"));
  const Question = React.lazy(
    () => import("../pages/components/dashBoard/questions/Question")
  );
  const SchoolDashBoard = React.lazy(
    () => import("../pages/components/dashBoard/SchoolDashBoard")
  );
  const AdminLayout = React.lazy(
    () => import("../layouts/adminLayout/AdminLayout")
  );
  const ProfileLayout = React.lazy(
    () => import("../layouts/profileLayout/ProfileLayout")
  );
  const CompleteProfile = React.lazy(
    () => import("../pages/components/completProfile/CompleteProfile")
  );
  const Profile = React.lazy(
    () => import("../pages/components/dashBoard/profile/Profile")
  );
  const ProfileAndSubmit = React.lazy(
    () => import("../pages/components/dashBoard/PreviewAndSubmit")
  );
  const PhotoPreviewAndSubmit = React.lazy(
    () => import("../pages/components/dashBoard/PhotoPreviewAndSubmit")
  );
  const RecognisedSchoolsNational = React.lazy(
    () => import ("../pages/RecognizedSchoolsNational.tsx")
  )

  const routes = createBrowserRouter([
    //  As of now i have commented this home page code in future we need to enable this one instaed od coming soon page
    {
      path: "/",
      errorElement: <RouterErrorFallback />,
      element: (
        <ScrollToTopWrapper>
          <ThemeProviderWrapper>
            <WebLayout />
          </ThemeProviderWrapper>
        </ScrollToTopWrapper>
      ),
      children: [
        {
          path: "",
          element: <Home />,
        },
      ],
    },
    // {
    //   path: "/",
    //   element: <ComingSoon />,
    // },
    {
      path: "account",
      errorElement: <RouterErrorFallback />,
      element: (
        <ScrollToTopWrapper>
          <ThemeProviderWrapper>
            <AuthLayout />
          </ThemeProviderWrapper>
        </ScrollToTopWrapper>
      ),
      children: [
        {
          path: "sign-up/:step",
          element: <SignIn />,
        },
        {
          path: "login",
          element: <SignIn />,
        },
      ],
    },
    {
      path: "complete-profile/:step",
      errorElement: <RouterErrorFallback />,
      element: <ProtectedRoute />,
      children: [
        {
          element: (
            <ScrollToTopWrapper>
              <ThemeProviderWrapper>
                <ProfileLayout />
              </ThemeProviderWrapper>
            </ScrollToTopWrapper>
          ),
          children: [
            {
              path: "",
              element: <CompleteProfile />,
            },
          ],
        },
      ],
    },
    {
      path: "school-dashboard",
      errorElement: <RouterErrorFallback />,
      element: <ProtectedRoute />,
      children: [
        {
          path: "",
          element: (
            <ScrollToTopWrapper>
              <ThemeProviderWrapper>
                <AdminLayout />
              </ThemeProviderWrapper>
            </ScrollToTopWrapper>
          ),
          children: [
            {
              path: "",
              element: <SchoolDashBoard />,
            },
            {
              path: "questions/:categoryId",
              element: <Question />,
            },
            {
              path: "profile",
              element: <Profile />,
            },
            {
              path: "preview-and-submit",
              element: <ProfileAndSubmit />,
            },
            {
              path: "photo-preview",
              element: <PhotoPreviewAndSubmit />,
            },
            {
              path: "category-preview/:categoryId",
              element: <CategoryPreview />,
            },
            {
              path: "preview",
              element: <QuestionPreview />,
            },
            // {
            //   path: "certificates",
            //   element: <DownloadCertificate />,
            // },
            // {
            //   path: "certificates/view",
            //   element: <CompleteSurvey />,
            // },
          ],
        },
      ],
    },
    {
      path: "/",
      element: (
        <ScrollToTopWrapper>
          <ThemeProviderWrapper>
            <PagesLayout />
          </ThemeProviderWrapper>
        </ScrollToTopWrapper>
      ),
      children: [
        {
          path: "about",
          element: <AboutSHVR />,
        },
        {
          path: "progress",
          element: <SHVRProgress />,
        },
        {
          path: "progress/district/:stateId",
          element: <ProgressDistrict />,
        },
        {
          path: "recognized-schools-national",
          element: <RecognisedSchoolsNational />,
        },
        {
          path: "faqs",
          element: <FAQPage />,
        },
        {
          path: "contact",
          element: <ContactUs />,
        },
        {
          path: "privacy-policy",
          element: <PrivacyPolicy />,
        },
        {
          path: "terms-and-conditions",
          element: <TermsAndConditions />,
        },
        {
          path: "copyright-policy",
          element: <CopyrightPolicy />,
        },
        {
          path: "hyperlink-policy",
          element: <HyperlinkPolicy />,
        },
        {
          path: "/resources",
          element: <Resources />,
        },
        // {
        //   path: "/certificates",
        //   element: <DownloadCertificate />,
        // },
        // {
        //   path: "/certificates/view",
        //   element: <CompleteSurvey />,
        // },
      ],
    },
    {
      path: "*",
      element: <NotPageFound />,
    },
    {
      path: "regional-user",
      element: <ProtectedAdminRoute />,
      errorElement: <RouterErrorFallback />,
      children: [
        {
          path: "",
          element: (
            <ScrollToTopWrapper>
              <RegionalUserLayout />
            </ScrollToTopWrapper>
          ),
          children: [
            {
              path: "dashboard",
              element: <RegionalUser />,
            },
            {
              path: "reports",
              element: <Reports />,
            },
            {
             path:"report-card",
             element:<ReportCatd/>
            },
            {
              path: "archived-portal",
              element: <ArchivedPortal />,
            },
            {
              path: "archived-portal/dashboard",
              element: <ArchivedDashboard />,
            },
            {
              path: "archived-portal/reports",
              element: <ArchivedReports />,
            },
            {
              path: "archived-portal/report-card",
              element: <ArchivedReportCard />,
            },
            {
              path: "archived-portal/recognition",
              element: <ArchivedRecognition />,
            },
            {
              path: "school-list",
              element: <SchoolList />,
            },
            {
              path: "school-survey-detail",
              element: <SchoolSurveyDetail />,
            },
            {
              path: "reports/progress",
              element: <TrackerTable />,
            },
            {
              path: "reports/tracker",
              element: <ReportsTracker />,
            },
            {
              path: "reports/evaluator",
              element: <EvaluatorTracker />,
            },
            {
              path: "reports/evaluator/view",
              element: <EvaluatorIndividualView />,
            },
            {
              path: "reports/committe-member",
              element: <CommitteMemberTracker />,
            },
            {
              path: "reports/committe-member/view",
              element: <CommittieMemberIndividualView />,
            },
            {
              path: "reports/evaluation",
              element: <Evaluation />,
              children: [
                {
                  path: "evaluator-status",
                  element: <DistrictNodalEvaluationReport />,
                },
                {
                  path: "state-evaluation-status",
                  element: <StateWiseEvaluationStatus />,
                },
                {
                  path: "district-evaluation-status",
                  element: <DistrictWiseEvaluationStatus />,
                },
                {
                  path: "detailed-report",
                  element: <DetailedReport />,
                },
                {
                  path: "school-evaluation-details/:id",
                  element: <SchoolEvaluationDetail />,
                },
              ],
            },
            {
              path: "reports/recognition-report",
              element: <RecognitionReport />,
            },
            {
              path: "reports/district-recognition",
              element: <DistrictRecognitionReportStateWise />,
            },
            {
              path: "reports/district-recognition/:stateId",
              element: <DistrictRecognitionReportDistrictWise />,
            },
            {
              path: "reports/state-recognition-report",
              element: <StateRecognitionReportStateWise />,
            },
            // Keep old routes for backward compatibility (can be removed later)
            {
              path: "reports/evaluation-status",
              element: <DistrictNodalEvaluationReport />,
            },
            {
              path: "reports/detailed-report",
              element: <DetailedReport />,
            },
            {
              path: "reports/state-wise-evaluation-status",
              element: <StateWiseEvaluationStatus />,
            },
            {
              path: "reports/district-wise-evaluation-status",
              element: <DistrictWiseEvaluationStatus />,
            },
            {
              path: "national",
              element: <NationalUser />,
              children: [
                {
                  path: "nodal-officers",
                  element: <NodalOfficers />,
                },
                {
                  path: "evaluators",
                  element: <Evaluators />,
                },
                {
                  path: "school-scorecard",
                  element: <ViewSchools />,
                },
                {
                  path: "committee-members",
                  element: <CommitteeMembers />,
                },
                {
                  path: "assign-school",
                  element: <AssignSchool />,
                },
                {
                  path: "assign-school/view/:id",
                  element: <AssignSchoolView />,
                },
                {
                  path: "eligible-schools",
                  element: <EligibleSchools />,
                },
                {
                  path: "recognition/select-school",
                  element: <RecognitionSelectSchool />,
                },
                {
                  path: "recognition/eligible-school",
                  element: <RecognitionEligibleSchools />,
                },
                // {
                //   path: "nodal-officers/add",
                //   element: <NodalOfficerForm type="add" />,
                // },
                // {
                //   path: "nodal-officers/edit",
                //   element: <NodalOfficerForm type="edit" />,
                // },
                {
                  path: "nodal-officers/view/:id",
                  element: <NodalOfficerView />,
                },
                {
                  path: "evaluators/add",
                  element: <EvaluatorsForm type="add" />,
                },
                {
                  path: "evaluators/edit",
                  element: <EvaluatorsForm type="edit" />,
                },
                {
                  path: "evaluators/view/:id",
                  element: <EvaluatorsView type="view" />,
                },
                {
                  path: "committee-members/add",
                  element: <CommitteeMemberForm type="add" />,
                },
                {
                  path: "committee-members/edit",
                  element: <CommitteeMemberForm type="edit" />,
                },
                {
                  path: "committee-members/view/:id",
                  element: <CommitteeMemberView type="view" />,
                },
                {
                  path: "state-nodal-officers",
                  element: <StateNodalOfficers />,
                },
                {
                  path: "state-nodal-officers/add",
                  element: <StateNodalOfficersForm type="add" />,
                },
                {
                  path: "state-nodal-officers/edit",
                  element: <StateNodalOfficersForm type="edit" />,
                },
                {
                  path: "state-nodal-officers/view/:id",
                  element: <StateNodalOfficersView />,
                },
                {
                  path: "recognition/eligible-school/school-evaluation-detail/:id",
                  element: <SchoolEvaluationDetail />,
                },
              ],
            },
            {
              path: "state",
              element: <StateUser />,
              children: [
                {
                  path: "evaluators",
                  element: <Evaluators />,
                },
                {
                  path: "committee-members",
                  element: <CommitteeMembers />,
                },
                {
                  path: "school-scorecard",
                  element: <ViewSchools />,
                },
                {
                  path: "district-nodal-officers",
                  element: <DistrictNodalOfficers />,
                },
                {
                  path: "district-nodal-officers/add",
                  element: <DistrictNodalOfficersForm type="add" />,
                },
                {
                  path: "district-nodal-officers/edit",
                  element: <DistrictNodalOfficersForm type="edit" />,
                },
                {
                  path: "district-nodal-officers/view/:id",
                  element: <DistrictNodalOfficersView type="view" />,
                },
                {
                  path: "evaluators/add",
                  element: <EvaluatorsForm type="add" />,
                },
                {
                  path: "evaluators/edit",
                  element: <EvaluatorsForm type="edit" />,
                },
                {
                  path: "committee-members/add",
                  element: <CommitteeMemberForm type="add" />,
                },
                {
                  path: "committee-members/edit",
                  element: <CommitteeMemberForm type="edit" />,
                },
                {
                  path: "committee-members/view/:id",
                  element: <CommitteeMemberView type="view" />,
                },
                {
                  path: "evaluators/view/:id",
                  element: <EvaluatorsView type="view" />,
                },
                {
                  path: "assign-school",
                  element: <AssignSchool />,
                },
                {
                  path: "assign-school/view/:id",
                  element: <AssignSchoolView />,
                },
                {
                  path: "eligible-schools",
                  element: <EligibleSchools />,
                },
                {
                  path: "recognition/select-school",
                  element: <RecognitionSelectSchool />,
                },
                {
                  path: "recognition/eligible-school",
                  element: <RecognitionEligibleSchools />,
                },
                {
                  path: "recognition/eligible-school/school-evaluation-detail/:id",
                  element: <SchoolEvaluationDetail />,
                },
                {
                  path: "user-manual",
                  element: <UserManual />,
                },
              ],
            },
            {
              path: "district",
              element: <DistrictUser />,
              children: [
                {
                  path: "evaluators",
                  element: <Evaluators />,
                },
                {
                  path: "committee-members",
                  element: <CommitteeMembers />,
                },
                {
                  path: "evaluators/add",
                  element: <EvaluatorsForm type="add" />,
                },
                {
                  path: "evaluators/edit",
                  element: <EvaluatorsForm type="edit" />,
                },
                {
                  path: "committee-members/add",
                  element: <CommitteeMemberForm type="add" />,
                },
                {
                  path: "committee-members/edit",
                  element: <CommitteeMemberForm type="edit" />,
                },
                {
                  path: "committee-members/view/:id",
                  element: <CommitteeMemberView type="view" />,
                },
                {
                  path: "evaluators/view/:id",
                  element: <EvaluatorsView type="view" />,
                },
                {
                  path: "nodal-officers",
                  element: <NodalOfficers />,
                },
                {
                  path: "nodal-officers/add",
                  element: <NodalOfficerForm type="add" />,
                },
                {
                  path: "nodal-officers/edit",
                  element: <NodalOfficerForm type="edit" />,
                },
                {
                  path: "nodal-officers/view/:id",
                  element: <NodalOfficerView />,
                },
                {
                  path: "assign-school",
                  element: <AssignSchool />,
                },
                {
                  path: "assign-school/view/:id",
                  element: <AssignSchoolView />,
                },
                {
                  path: "eligible-schools",
                  element: <EligibleSchools />,
                },
                {
                  path: "recognition/select-school",
                  element: <RecognitionSelectSchool />,
                },
                {
                  path: "recognition/eligible-school",
                  element: <RecognitionEligibleSchools />,
                },
                {
                  path: "user-manual",
                  element: <UserManual />,
                },
                {
                  path: "recognition/eligible-school/school-evaluation-detail/:id",
                  element: <SchoolEvaluationDetail />,
                },
                {
                  path: "school-scorecard",
                  element: <ViewSchools />,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "evaluator",
      element: <ProtectedEvaluatorRoute />,
      errorElement: <RouterErrorFallback />,
      children: [
        {
          path: "",
          element: (
            <ScrollToTopWrapper>
              <ThemeProviderWrapper>
                <EvaluatorLayout />
              </ThemeProviderWrapper>
            </ScrollToTopWrapper>
          ),
          children: [
            {
              path: "dashboard",
              element: <Dashboard />,
            },
            {
              path: "review-school-submission/:id",
              element: <ReviewSchoolSubmission />,
            },
            {
              path: "view/:id",
              element: <ReviewSchoolSubmission />,
            },
          ],
        },
      ],
    },
  ]);

  const maintenaceRoutes = createBrowserRouter([
    {
      path: "/",
      element: <Maintenance />,
    },
  ]);
  return (
    <Suspense fallback={<PageLoader />}>
      <RouterProvider router={routes} />
    </Suspense>
  );
};

export default AppRoutes;
